import type {FinalValues as InvoiceFinalValues} from "../@types/invoice";
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const InvoiceStatus = {
    Draft: "D",
    Final: "F"
};
export type InvoiceStatusType = typeof InvoiceStatus[keyof typeof InvoiceStatus];
export const SentUsing = {
    Email: "Email",
    Mail: "Mail"
};
export type SentUsingType = typeof SentUsing[keyof typeof SentUsing];
class Invoice extends Base implements EntityType {
    invoiceId: uuid;
    additionalFundingNeeded: number;
    invoiceNumber: string;
    clientIds: uuid[];
    leadId?: uuid | null;
    matterId?: uuid | null;
    description?: string | null;
    startDate?: Date | null;
    endDate: Date;
    issueDate: Date;
    finalizedOn?: Date | null;
    dueDate: Date;
    includeUnbilled: boolean;
    primaryTax?: number | null;
    secondaryTax?: number | null;
    invoiceStatus?: InvoiceStatusType | null;
    terms?: number | null;
    taskIds: uuid[];
    expenseIds: uuid[];
    transactionIds: uuid[];
    flatFeeIds: uuid[];
    flatFeeMilestoneIds: uuid[];
    totalBilled?: number | null;
    totalDue?: number | null;
    amountPaid?: number | null;
    taskPositions: uuid[];
    billingIncrement: number;
    roundingMethod: string;
    sentDate?: Date | null;
    sentUsing?: SentUsingType | null;
    reconcileNotes?: string | null;
    isReconciled: boolean;
    finalValues?: InvoiceFinalValues | null;
    constructor(doc: typeof Invoice | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            invoiceId: genUUID(),
            invoiceNumber: null,
            clientIds: [],
            endDate: null,
            issueDate: null,
            dueDate: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "invoice";
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || genUUID();
        // @ts-ignore
        this.additionalFundingNeeded = util.coalesce(util.parseFloat(doc._additionalFundingNeeded), util.parseFloat(doc.additionalFundingNeeded), util.parseFloat(doc.additionalfundingneeded), 0);
        // @ts-ignore
        this.invoiceNumber = doc._invoiceNumber || doc.invoiceNumber || doc.invoicenumber || null;
        // @ts-ignore
        this.clientIds = doc._clientIds || doc.clientIds || doc.clientids || [];
        // @ts-ignore
        this.leadId = doc._leadId || doc.leadId || doc.leadid || null;
        // @ts-ignore
        this.matterId = doc._matterId || doc.matterId || doc.matterid || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.startDate = DateUtil.dateOnly(doc._startDate) || DateUtil.dateOnly(doc.startDate) || DateUtil.dateOnly(doc.startdate) || null;
        // @ts-ignore
        this.endDate = DateUtil.dateOnly(doc._endDate) || DateUtil.dateOnly(doc.endDate) || DateUtil.dateOnly(doc.enddate) || null;
        // @ts-ignore
        this.issueDate = DateUtil.dateOnly(doc._issueDate) || DateUtil.dateOnly(doc.issueDate) || DateUtil.dateOnly(doc.issuedate) || null;
        // @ts-ignore
        this.finalizedOn = DateUtil.dateOnly(doc._finalizedOn) || DateUtil.dateOnly(doc.finalizedOn) || DateUtil.dateOnly(doc.finalizedon) || null;
        // @ts-ignore
        this.dueDate = DateUtil.dateOnly(doc._dueDate) || DateUtil.dateOnly(doc.dueDate) || DateUtil.dateOnly(doc.duedate) || null;
        // @ts-ignore
        this.includeUnbilled = util.defaultBoolean([doc.includeUnbilled, doc._includeUnbilled, doc.includeunbilled, true]);
        // @ts-ignore
        this.primaryTax = util.coalesce(util.parseFloat(doc._primaryTax), util.parseFloat(doc.primaryTax), util.parseFloat(doc.primarytax), null);
        // @ts-ignore
        this.secondaryTax = util.coalesce(util.parseFloat(doc._secondaryTax), util.parseFloat(doc.secondaryTax), util.parseFloat(doc.secondarytax), null);
        // @ts-ignore
        this.invoiceStatus = util.trimRight(doc._invoiceStatus) || util.trimRight(doc.invoiceStatus) || util.trimRight(doc.invoicestatus) || null;
        // @ts-ignore
        this.terms = util.coalesce(util.parseInt(doc._terms), util.parseInt(doc.terms), util.parseInt(doc.terms), null);
        // @ts-ignore
        this.taskIds = doc._taskIds || doc.taskIds || doc.taskids || [];
        // @ts-ignore
        this.expenseIds = doc._expenseIds || doc.expenseIds || doc.expenseids || [];
        // @ts-ignore
        this.transactionIds = doc._transactionIds || doc.transactionIds || doc.transactionids || [];
        // @ts-ignore
        this.flatFeeIds = doc._flatFeeIds || doc.flatFeeIds || doc.flatfeeids || [];
        // @ts-ignore
        this.flatFeeMilestoneIds = doc._flatFeeMilestoneIds || doc.flatFeeMilestoneIds || doc.flatfeemilestoneids || [];
        // @ts-ignore
        this.totalBilled = util.coalesce(util.parseFloat(doc._totalBilled), util.parseFloat(doc.totalBilled), util.parseFloat(doc.totalbilled), null);
        // @ts-ignore
        this.totalDue = util.coalesce(util.parseFloat(doc._totalDue), util.parseFloat(doc.totalDue), util.parseFloat(doc.totaldue), null);
        // @ts-ignore
        this.amountPaid = util.coalesce(util.parseFloat(doc._amountPaid), util.parseFloat(doc.amountPaid), util.parseFloat(doc.amountpaid), null);
        // @ts-ignore
        this.taskPositions = doc._taskPositions || doc.taskPositions || doc.taskpositions || [];
        // @ts-ignore
        this.billingIncrement = util.coalesce(util.parseInt(doc._billingIncrement), util.parseInt(doc.billingIncrement), util.parseInt(doc.billingincrement), 1);
        // @ts-ignore
        this.roundingMethod = doc._roundingMethod || doc.roundingMethod || doc.roundingmethod || "Round Up Past Halfway Point";
        // @ts-ignore
        this.sentDate = DateUtil.dateOnly(doc._sentDate) || DateUtil.dateOnly(doc.sentDate) || DateUtil.dateOnly(doc.sentdate) || null;
        // @ts-ignore
        this.sentUsing = doc._sentUsing || doc.sentUsing || doc.sentusing || null;
        // @ts-ignore
        this.reconcileNotes = doc._reconcileNotes || doc.reconcileNotes || doc.reconcilenotes || null;
        // @ts-ignore
        this.isReconciled = util.defaultBoolean([doc.isReconciled, doc._isReconciled, doc.isreconciled, false]);
        // @ts-ignore
        this.finalValues = doc._finalValues || doc.finalValues || doc.finalvalues || {};
    }
    get id(): uuid { return this.invoiceId; }
    addClientId(clientId: uuid): uuid {
        if (!clientId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.clientIds) this.clientIds = [];
        this.clientIds.push(clientId);
        return clientId;
    }
    updateClientId(clientId: uuid) {
        if (!this.clientIds) this.clientIds = [];
        const index = this.clientIds.findIndex(item => item === clientId);
        if (index !== -1) { this.clientIds[index] = clientId; }
        else this.addClientId(clientId);
    }
    removeClientId(clientId: uuid) {
        if (!this.clientIds) return;
        let foundItems = this.clientIds?.filter(item => item === clientId);
        if (foundItems && foundItems.length > 0) {
            this.clientIds = this.clientIds.filter(item => !(item === clientId));
        }
    }
    abandonClientIds() { this.clientIds = []; }
    static INVOICE_STATUS_VALUES = ["D", "F"];
    addTaskId(taskId: uuid): uuid {
        if (!taskId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.taskIds) this.taskIds = [];
        this.taskIds.push(taskId);
        return taskId;
    }
    updateTaskId(taskId: uuid) {
        if (!this.taskIds) this.taskIds = [];
        const index = this.taskIds.findIndex(item => item === taskId);
        if (index !== -1) { this.taskIds[index] = taskId; }
        else this.addTaskId(taskId);
    }
    removeTaskId(taskId: uuid) {
        if (!this.taskIds) return;
        let foundItems = this.taskIds?.filter(item => item === taskId);
        if (foundItems && foundItems.length > 0) {
            this.taskIds = this.taskIds.filter(item => !(item === taskId));
        }
    }
    abandonTaskIds() { this.taskIds = []; }
    addExpenseId(expenseId: uuid): uuid {
        if (!expenseId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.expenseIds) this.expenseIds = [];
        this.expenseIds.push(expenseId);
        return expenseId;
    }
    updateExpenseId(expenseId: uuid) {
        if (!this.expenseIds) this.expenseIds = [];
        const index = this.expenseIds.findIndex(item => item === expenseId);
        if (index !== -1) { this.expenseIds[index] = expenseId; }
        else this.addExpenseId(expenseId);
    }
    removeExpenseId(expenseId: uuid) {
        if (!this.expenseIds) return;
        let foundItems = this.expenseIds?.filter(item => item === expenseId);
        if (foundItems && foundItems.length > 0) {
            this.expenseIds = this.expenseIds.filter(item => !(item === expenseId));
        }
    }
    abandonExpenseIds() { this.expenseIds = []; }
    addTransactionId(transactionId: uuid): uuid {
        if (!transactionId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.transactionIds) this.transactionIds = [];
        this.transactionIds.push(transactionId);
        return transactionId;
    }
    updateTransactionId(transactionId: uuid) {
        if (!this.transactionIds) this.transactionIds = [];
        const index = this.transactionIds.findIndex(item => item === transactionId);
        if (index !== -1) { this.transactionIds[index] = transactionId; }
        else this.addTransactionId(transactionId);
    }
    removeTransactionId(transactionId: uuid) {
        if (!this.transactionIds) return;
        let foundItems = this.transactionIds?.filter(item => item === transactionId);
        if (foundItems && foundItems.length > 0) {
            this.transactionIds = this.transactionIds.filter(item => !(item === transactionId));
        }
    }
    abandonTransactionIds() { this.transactionIds = []; }
    addFlatFeeId(flatFeeId: uuid): uuid {
        if (!flatFeeId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.flatFeeIds) this.flatFeeIds = [];
        this.flatFeeIds.push(flatFeeId);
        return flatFeeId;
    }
    updateFlatFeeId(flatFeeId: uuid) {
        if (!this.flatFeeIds) this.flatFeeIds = [];
        const index = this.flatFeeIds.findIndex(item => item === flatFeeId);
        if (index !== -1) { this.flatFeeIds[index] = flatFeeId; }
        else this.addFlatFeeId(flatFeeId);
    }
    removeFlatFeeId(flatFeeId: uuid) {
        if (!this.flatFeeIds) return;
        let foundItems = this.flatFeeIds?.filter(item => item === flatFeeId);
        if (foundItems && foundItems.length > 0) {
            this.flatFeeIds = this.flatFeeIds.filter(item => !(item === flatFeeId));
        }
    }
    abandonFlatFeeIds() { this.flatFeeIds = []; }
    addFlatFeeMilestoneId(flatFeeMilestoneId: uuid): uuid {
        if (!flatFeeMilestoneId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.flatFeeMilestoneIds) this.flatFeeMilestoneIds = [];
        this.flatFeeMilestoneIds.push(flatFeeMilestoneId);
        return flatFeeMilestoneId;
    }
    updateFlatFeeMilestoneId(flatFeeMilestoneId: uuid) {
        if (!this.flatFeeMilestoneIds) this.flatFeeMilestoneIds = [];
        const index = this.flatFeeMilestoneIds.findIndex(item => item === flatFeeMilestoneId);
        if (index !== -1) { this.flatFeeMilestoneIds[index] = flatFeeMilestoneId; }
        else this.addFlatFeeMilestoneId(flatFeeMilestoneId);
    }
    removeFlatFeeMilestoneId(flatFeeMilestoneId: uuid) {
        if (!this.flatFeeMilestoneIds) return;
        let foundItems = this.flatFeeMilestoneIds?.filter(item => item === flatFeeMilestoneId);
        if (foundItems && foundItems.length > 0) {
            this.flatFeeMilestoneIds = this.flatFeeMilestoneIds.filter(item => !(item === flatFeeMilestoneId));
        }
    }
    abandonFlatFeeMilestoneIds() { this.flatFeeMilestoneIds = []; }
    addTaskPosition(taskPosition: uuid): uuid {
        if (!taskPosition) throw new ValidationError('No nulls allowed in an array!');
        if (!this.taskPositions) this.taskPositions = [];
        this.taskPositions.push(taskPosition);
        return taskPosition;
    }
    updateTaskPosition(taskPosition: uuid) {
        if (!this.taskPositions) this.taskPositions = [];
        const index = this.taskPositions.findIndex(item => item === taskPosition);
        if (index !== -1) { this.taskPositions[index] = taskPosition; }
        else this.addTaskPosition(taskPosition);
    }
    removeTaskPosition(taskPosition: uuid) {
        if (!this.taskPositions) return;
        let foundItems = this.taskPositions?.filter(item => item === taskPosition);
        if (foundItems && foundItems.length > 0) {
            this.taskPositions = this.taskPositions.filter(item => !(item === taskPosition));
        }
    }
    abandonTaskPositions() { this.taskPositions = []; }
    static SENT_USING_VALUES = ["Email", "Mail"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this invoice"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.invoiceId; }
    displayFn() { return `${this.invoiceNumber} [${this.invoiceStatus === 'D' ? 'Draft' : 'Finalized'}]`; }
    static override getNonRefArrayFields(): string[] { return ["clientIds", "taskIds", "expenseIds", "transactionIds", "flatFeeIds", "flatFeeMilestoneIds", "taskPositions"]; }
    static override getDefaultEntries(): Invoice[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'invoice',
    dataArea: 'Billing',
    refreshDraftTotalsOnSave: true,
    preserveReferencesWhen: {field: 'invoiceStatus', equals: 'F', related: ['transactionIds']},
    table: 'INVOICE',
    comment: 'A billing statement issued by the law firm to one or more clients, covering a date range of billable time entries (tasks), expenses, flat fees, and trust/payment transactions. Invoices may be tied to a lead (for initial-consult charges) or to one or more matters; they exist in Draft or Finalized states, and at finalization a frozen snapshot (finalValues) is captured so the invoice can be rendered without live entity lookups.',
    fields: {
        invoiceId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this invoice'
        },

        // invoiceBatchId: {
        //     type: 'uuid', entityType: 'invoiceBatch', comment: 'Related invoiceBatch ID'
        // },

        /**
         * If the matter has a minimum required trust balance, and the trust is under that amount, the owner can choose
         * to ask for an amount lower or higher than that amount.
         */
        additionalFundingNeeded: {
            type: 'money', required: false, default: 0, comment: 'If additional trust funding is required, how much?'
        },

        /**
         * A unique number for the invoice, so that the client can reference the invoice by a unique ID (NOT a UUID!)
         */
        invoiceNumber: {
            type: 'string', required: true, comment: 'A unique ID for this invoice'
        },

        /**
         * Which clients are included on this invoice?  If multiple clients are selected, they MUST share one or more matters.
         */
        clientIds: {
            type: 'uuid[]', entityType: 'contact', deleteStrategy: 'preserve', required: true, comment: 'Which client(s) are included on this invoice'
        },

        /**
         * An invoice for an initial consult is stored against a lead.  If the lead converts to a case, we can add matterId
         * as well.
         */
        leadId: {
            type: 'uuid', entityType: 'lead', deleteStrategy: 'preserve', required: false, comment: 'If this is an invoice for an initial consult, the leadId goes here'
        },

        /**
         * The one Case this invoice belongs to. An initial-consult invoice is stored against a lead and has none until
         * the lead converts.
         */
        matterId: {
            type: 'uuid', entityType: 'matter', deleteStrategy: 'preserve', required: false, comment: 'The Case this invoice belongs to'
        },

        /**
         * The description for this invoice, shown to the client on the invoice.
         */
        description: {
            type: 'string', comment: 'Notes to the client on the invoice'
        },

        /**
         * First date for which this invoice cycle will look for activities, expenses, and transactions.
         * A null value means we should look for any stragglers.
         */
        startDate: {
            type: 'date', comment: 'Start date for activities/expenses included in this invoice'
        },

        /**
         * aka Invoice Through Date.  Last day of the invoicing period.
         */
        endDate: {
            type: 'date', required: true, comment: 'End date for activities/expenses included in this invoice'
        },

        /**
         * The date the invoice was issued
         */
        issueDate: {
            type: 'date', required: true, comment: 'The date the invoice was run (as of date)'
        },

        /**
         * The date the invoice was finalized.
         */
        finalizedOn: {
            type: 'date', comment: 'The date the invoice was finalized'
        },

        /**
         * When the invoice's due date is set, based on terms
         */
        dueDate: {
            type: 'date', required: true, comment: 'The date the invoice must be paid'
        },

        /**
         * Should we include unbilled/$0 items on the invoice?  Note that this will not override the
         * 'do not include on invoice' if marked on a task.
         */
        includeUnbilled: {
            type: 'boolean', default: true, comment: 'Should unbilled/$0 items be included?'
        },

        /**
         * Primary tax (NOT the rate, this is the actual amount).
         */
        primaryTax: {
            type: 'money', comment: 'If the firm is based in a state that collects tax, this is the primary tax'
        },

        /**
         * Secondary tax (NOT the rate, this is the actual amount).
         */
        secondaryTax: {
            type: 'money', comment: 'If the firm is based in a state that collects tax, this is the secondary tax'
        },

        /**
         * Status of the invoice.
         */
        invoiceStatus: {
            type: 'char',
            values: ['D', 'F'],
            valueNames: ['Draft', 'Final'],
            comment: 'Status of the invoice ([D]raft, [F]inalized)'
        },

        /**
         * e.g. days in term, eg. 30 would mean Net 30.  0 means due on receipt.
         */
        terms: {
            type: 'int',
            comment: 'Days in Payment terms (e.g. 30 would be Net 30)'
        },

        /**
         * The tasks included in this invoice.
         */
        taskIds: {
            type: 'uuid[]',
            entityType: 'task',
            deleteStrategy: 'preserve', // OWA-830: a draft's id arrays are empty, so any match is a finalized invoice, and a finalized invoice never changes
            comment: 'IDs of the tasks that will be included on this invoice'
        },

        /**
         * The expenses included in this invoice.  We can't use a ref field here, because
         * expenses are already used as a ref field for matter.  Plus, we want to be very
         * specific.
         */
        expenseIds: {
            type: 'uuid[]',
            entityType: 'expense',
            deleteStrategy: 'preserve', // OWA-830: a draft's id arrays are empty, so any match is a finalized invoice, and a finalized invoice never changes
            comment: 'IDs of the expenses that will be included on this invoice'
        },

        /**
         * Transactions to include in this statement, including payments, trust transfers and balance adjustments.
         */
        transactionIds: {
            type: 'uuid[]',
            entityType: 'transaction',
            deleteStrategy: 'preserve', // OWA-830: a draft's id arrays are empty, so any match is a finalized invoice, and a finalized invoice never changes
            comment: 'IDs of transactions on this bill'
        },

        /**
         * Flat fees to include in this statement.
         */
        flatFeeIds: {
            type: 'uuid[]',
            entityType: 'flatFee',
            deleteStrategy: 'preserve', // OWA-830: a draft's id arrays are empty, so any match is a finalized invoice, and a finalized invoice never changes
            comment: 'IDs of flat fees on this bill'
        },

        /**
         * Flat fee milestones to include in this statement.
         */
        flatFeeMilestoneIds: {
            type: 'uuid[]',
            entityType: 'flatFeeMilestone',
            deleteStrategy: 'preserve', // OWA-830: a draft's id arrays are empty, so any match is a finalized invoice, and a finalized invoice never changes
            comment: 'IDs of flat fee milestones on this bill'
        },

        /**
         * The total amount of the bill, including labor and expenses, minus discounts.  This does not include
         * transactions!  This is the amount of work done.
         */
        totalBilled: {
            type: 'money', comment: 'Total amount billed to the client'
        },

        /**
         * Total amount due from the client, taking in to account all fees, expenses, discounts, payments, adjustments, etc.
         */
        totalDue: {
            type: 'money', comment: 'The total amount the client still owes the firm on this invoice'
        },

        /**
         * Amount of money applied to this invoice so far.  This is important, because we may take a payment and apply the
         * money only to expenses for unpaid invoices, which will leave several invoices partially paid.
         *
         * TODO: Is this necessary?  We can generate this on the fly, and not have to worry about
         *       keeping it up to date.
         */
        amountPaid: {
            type: 'money', comment: 'Amount of money applied to this invoice so far'
        },

        /**
         * Tasks within an invoice may have positioning that does not follow the completionDate timestamp,
         * in order to make the invoice more readable.
         */
        taskPositions: {
            type: 'uuid[]',
            entityType: 'task',
            deleteStrategy: 'preserve',
            comment: 'An ordered array of tasks for the invoice, if not by date'
        },

        /**
         * The billing increment for this invoice, in whole minutes
         */
        billingIncrement: {
            type: 'int', default: 1, comment: 'Billing increment, in minutes'
        },

        /**
         * The rounding method used for this invoice (see OptionConstants.js for values)
         */
        roundingMethod: {
            type: 'string', default: 'Round Up Past Halfway Point', comment: 'Method used for rounding time entries'
        },

        /**
         * Date the invoice was sent to the client
         */
        sentDate: {
            type: 'date', comment: 'The date the invoice was sent'
        },

        /**
         * The method used to send the invoice to the client
         */
        sentUsing: {
            type: 'string', values: ['Email', 'Mail'], comment: 'How the invoice was sent (email, physical)'
        },

        /**
         Notes from the bookkeeper on reconciliation
         */
        reconcileNotes: {
            type: 'string', comment: 'Notes from the bookkeeper on reconciliation'
        },

        /**
         Has this invoice been reconciled by the bookkeeper?
         */
        isReconciled: {
            type: 'boolean', default: false, comment: 'Has this invoice been reconciled by the bookkeeper?'
        },

        /**
         * Frozen snapshot of all computed invoice data at finalization time.
         * When present, the entire invoice can be rendered from this field alone,
         * without any live entity lookups.
         */
        finalValues: {
            type: 'jsonb',
            jsdoc: 'InvoiceFinalValues',
            comment: 'Frozen snapshot of all invoice data at finalization time'
        }

    },
    requiredTypes: ['invoiceFinal'],
    displayFn: "`${this.invoiceNumber} [${this.invoiceStatus === 'D' ? 'Draft' : 'Finalized'}]`",
    indexes: [
        {name: 'notDeleted', fn: m => m.status !== 'D'},
        {name: 'finalized', fn: m => !!m.finalizedOn && m.status !== 'D'},
        {name: 'draft', fn: m => !m.finalizedOn && m.status !== 'D'},
    ],
    maps: [
        {
            name: 'matterId',
            keysFn: i => i.matterId,
            presentFn: (i, k) => i.leadId ? i.leadId === k : i.matterId === k && i.status !== 'D'
        },
        {name: 'clientIds', keysFn: i => i.clientIds, presentFn: (i, k) => i.clientIds?.includes(k)},
        {name: 'leadId', keysFn: i => i.leadId, presentFn: (i, k) => i.leadId === k},
        {name: 'taskIds', keysFn: i => i.taskIds, presentFn: (i, k) => i.taskIds?.includes(k)},
        {name: 'taskPositions', keysFn: i => i.taskPositions, presentFn: (i, k) => i.taskPositions?.includes(k)},
        {name: 'expenseIds', keysFn: i => i.expenseIds, presentFn: (i, k) => i.expenseIds?.includes(k)},
        {name: 'transactionIds', keysFn: i => i.transactionIds, presentFn: (i, k) => i.transactionIds?.includes(k)},
        {name: 'flatFeeIds', keysFn: i => i.flatFeeIds, presentFn: (i, k) => i.flatFeeIds?.includes(k)},
        {name: 'flatFeeMilestoneIds', keysFn: i => i.flatFeeMilestoneIds, presentFn: (i, k) => i.flatFeeMilestoneIds?.includes(k)},
    ]
});
    }
}
export default Invoice;
